import React, { useState, useRef } from 'react';
import './Navigation.css';

export function Navigation({ showPlayer, isPlaying, onPlayPause, volume, onVolumeChange, currentTrack, currentSection }) {
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);
  const [previousVolume, setPreviousVolume] = useState(0.7);
  const [activeBubbleStyle, setActiveBubbleStyle] = useState({ opacity: 0 });
  const [hoverBubbleStyle, setHoverBubbleStyle] = useState({ opacity: 0 });
  const navRef = useRef(null);
  const linkRefs = useRef([]);

  const handleVolumeClick = () => {
    if (volume === 0) {
      onVolumeChange(previousVolume);
    } else {
      setPreviousVolume(volume);
      onVolumeChange(0);
    }
  };

  const iconColor = 'white';

  const navLinks = [
    { href: '#home', label: 'Home', section: 0 },
    { href: '#player', label: 'Player', section: 1 },
    { href: '#explain', label: 'Das Konzept', section: 2 },
    { href: '#schreib-uns', label: 'Schreib uns', section: 3 },
    { href: '#features', label: 'Features', section: 4 },
    { href: '#kontakt', label: 'Über uns', section: 6 },
  ];

  // Calculate bubble position for a link
  const getBubblePosition = (linkElement) => {
    if (!linkElement || !navRef.current) return null;
    const linkRect = linkElement.getBoundingClientRect();
    const navRect = navRef.current.getBoundingClientRect();
    return {
      left: linkRect.left - navRect.left,
      top: linkRect.top - navRect.top,
      width: linkRect.width,
      height: linkRect.height,
      opacity: 1,
    };
  };

  // Update active bubble when section changes
  React.useEffect(() => {
    // Find closest section that's <= currentSection
    const activeLink = navLinks
      .filter(link => link.section <= currentSection)
      .sort((a, b) => b.section - a.section)[0];
    const activeIndex = activeLink ? navLinks.indexOf(activeLink) : 0;

    if (linkRefs.current[activeIndex]) {
      const pos = getBubblePosition(linkRefs.current[activeIndex]);
      if (pos) setActiveBubbleStyle(pos);
    }
  }, [currentSection]);

  // Initial position after mount - only runs once
  React.useEffect(() => {
    const timer = setTimeout(() => {
      const activeIndex = navLinks.findIndex(link => link.section === currentSection);
      if (activeIndex !== -1 && linkRefs.current[activeIndex]) {
        const pos = getBubblePosition(linkRefs.current[activeIndex]);
        if (pos) setActiveBubbleStyle(pos);
      }
    }, 100);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMouseEnter = (index) => {
    const pos = getBubblePosition(linkRefs.current[index]);
    if (pos) setHoverBubbleStyle(pos);
  };

  const handleMouseLeave = () => {
    setHoverBubbleStyle(prev => ({ ...prev, opacity: 0 }));
  };

  return (
    <div className="nav-container hidden md:fixed top-4 left-1/2 -translate-x-1/2 z-40 md:block">
      <div className="nav-wrap">
        <nav className="nav" ref={navRef}>
          {/* Bubbles */}
          <div className="bubble active" style={activeBubbleStyle} />
          <div className="bubble hover" style={hoverBubbleStyle} />

          {/* Nav Links */}
          {navLinks.map(({ href, label, section }, index) => {
            // Find closest section that's <= currentSection
            const activeSection = navLinks
              .filter(link => link.section <= currentSection)
              .sort((a, b) => b.section - a.section)[0]?.section;
            const isActive = section === activeSection;
            return (
              <a
                key={href}
                ref={el => linkRefs.current[index] = el}
                href={href}
                className={isActive ? 'active' : ''}
                onMouseEnter={() => handleMouseEnter(index)}
                onMouseLeave={handleMouseLeave}
              >
                {label}
              </a>
            );
          })}

          {/* Player Controls */}
          {showPlayer && (
            <div className="nav-player">
            <button
              onClick={onPlayPause}
              className="play-button"
              aria-label={isPlaying ? 'Pause' : 'Play'}
            >
              {isPlaying ? (
                <svg width="10" height="10" viewBox="0 0 16 16" fill="none">
                  <rect x="3" y="2" width="3" height="12" fill="white" rx="1"/>
                  <rect x="10" y="2" width="3" height="12" fill="white" rx="1"/>
                </svg>
              ) : (
                <svg width="10" height="10" viewBox="0 0 16 16" fill="none">
                  <path d="M4 2L13 8L4 14V2Z" fill="white"/>
                </svg>
              )}
            </button>

            <div
              className="volume-control"
              onMouseEnter={() => setShowVolumeSlider(true)}
              onMouseLeave={() => setShowVolumeSlider(false)}
            >
              <button
                onClick={handleVolumeClick}
                className="volume-button"
                aria-label="Volume"
              >
                {volume === 0 ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M11 5L6 9H2v6h4l5 4V5z" fill={iconColor}/>
                    <line x1="23" y1="9" x2="17" y2="15" stroke={iconColor} strokeWidth="2" strokeLinecap="round"/>
                    <line x1="17" y1="9" x2="23" y2="15" stroke={iconColor} strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                ) : volume < 0.5 ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M11 5L6 9H2v6h4l5 4V5z" fill={iconColor}/>
                    <path d="M15.54 8.46a5 5 0 010 7.07" stroke={iconColor} strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M11 5L6 9H2v6h4l5 4V5z" fill={iconColor}/>
                    <path d="M15.54 8.46a5 5 0 010 7.07M19.07 4.93a10 10 0 010 14.14" stroke={iconColor} strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                )}
              </button>

              <div
                className="volume-slider-wrap"
                style={{
                  width: showVolumeSlider ? '80px' : '0px',
                  opacity: showVolumeSlider ? 1 : 0
                }}
              >
                <div
                  className="volume-slider-fill"
                  style={{ width: `${volume * 100}%` }}
                />
                <div
                  className="volume-slider-thumb"
                  style={{ left: `calc(${volume * 100}% - 6px)` }}
                />
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={volume * 100}
                  onChange={(e) => onVolumeChange(e.target.value / 100)}
                  className="volume-slider-input"
                />
              </div>
            </div>
          </div>
          )}
        </nav>
      </div>
    </div>
  );
}
